<template>
  <div>
    <b-field
      v-for="category in productConfigData.availableOptions"
      :key="`opt_category_${category.id}`"
      :label="category.name + (category.required ? ' *' : '')"
      :type="{ 'is-danger': hasError(`opt_category_${category.id}`) }"
      :message="{
        [getFirstError(`opt_category_${category.id}`)]: hasError(
          `opt_category_${category.id}`
        )
      }"
    >
      <ValidationProvider
        :vid="`opt_category_${category.id}`"
        :rules="`${category.required ? 'required' : ''}`"
        :custom-messages="{
          required: $t('_.this_is_a_required_configuration')
        }"
        slim
      >
        <selector
          :key="`opt_category_${category.id}`"
          v-model="productConfigData.selectedOptions[category.id]"
          :native-value-path="`id`"
          :items="category.options"
          :multiselect="category.options.length === 1 || category.multiple"
          :name="`opt_category_${category.id}`"
          :type="hasError(`opt_category_${category.id}`) ? 'is-danger' : null"
          :disabled="
            (category.options.length === 1 && category.required) || disabled
          "
        >
          <template v-slot:left-label="{ item, selected }">
            <p class="u-option-details">
              <span>{{ item.name }}</span>
              <!-- Price override tooltip -->
              <b-tooltip
                v-if="$_.get(category, 'price_override', false)"
                :label="$t('_sentence.override_price_product_option_desc')"
                type="is-dark"
                multilined
              >
                <span class="has-text-dark is-size-8">
                  <b-icon icon="random" size="is-small" class="is-marginless" />
                  <i18n path="_.overrides_price" />
                </span>
              </b-tooltip>
              <!-- 'Custom price' tag -->
              <b-tag
                v-if="selected && !$_.isNil(item.custom_price)"
                type="is-caution"
              >
                <b-icon icon="pen" size="is-small" />
                <i18n path="_.custom_price" />
              </b-tag>
              <!-- Promotion stars (eg. Black Friday Sale) -->
              <template v-else-if="item.saving || item.hasMixedPromotions">
                <b-tooltip
                  v-for="(promo, pI) in getPromoTags(item)"
                  :key="`promo-${pI}`"
                  :label="promo.tooltip"
                  :size="item.hasMixedPromotions ? 'is-medium' : 'is-small'"
                  type="is-danger"
                  multilined
                >
                  <span class="has-text-danger has-cursor-help is-size-8">
                    <b-icon icon="star" size="is-small" class="is-marginless" />
                    <span>{{ promo.label }} </span>
                  </span>
                </b-tooltip>
              </template>
            </p>
          </template>
          <template v-slot:right-label="{ item, selected }">
            <div v-if="!hidePrices" class="is-flex has-items-aligned-center">
              <del
                v-if="
                  (item.price_discounted || item.price_discounted === 0) &&
                  (!selected || $_.isNil(item.custom_price))
                "
                class="is-size-7 has-text-grey-light is-italic has-margin-right-50"
                >{{
                  `+ ${$store.getters["utils/money/trimTrailingZeros"](
                    item.price_formatted
                  )}`
                }}</del
              >
              <editable-price
                :key="`opt_category_${category.id}:${item.id}`"
                v-model="productConfigData.prices[item.id]"
                :value-formatted="
                  $_.without(
                    [
                      selected && item.custom_price_formatted,
                      item.price_discounted_formatted,
                      item.price_formatted
                    ],
                    undefined,
                    false,
                    null
                  )[0]
                "
                :original-value="
                  item.price_discounted_formatted
                    ? item.price_discounted
                    : item.price
                "
                :original-value-formatted="
                  item.price_discounted_formatted || item.price_formatted
                "
                :currency="getCurrency(productConfigData.currencyId)"
                :instruction="
                  $t('_sentence.override_selling_price', {
                    object: $t('_.product_option').toString().toLowerCase(),
                    taxType: $tc(
                      '_.price_tax_type_switch',
                      productConfigData.taxType
                    )
                  })
                "
                :can-edit="selected && isAdmin"
                :is-disabled="disabled"
                prefix="+"
                :suffix="
                  productConfigData.selectedBillingTerm !==
                  item.billing_cycle_months
                    ? `(${(getBillingCycleName(item.billing_cycle_months) || '')
                        .toString()
                        .toLowerCase()})`
                    : ''
                "
                @click.native="handleClick($event, selected)"
                @input="
                  $event === (item.price_discounted || item.price) &&
                    $delete(productConfigData.prices, item.id)
                "
              />
              <!-- Quantity -->
              <template v-if="item.order_type === orderTypes.QUANTITY_BASED">
                <div v-if="selected" class="has-margin-left-100">
                  <ValidationProvider
                    tag="div"
                    :vid="`opt_category_${category.id}`"
                    :name="$t('_.quantity').toString().toLowerCase()"
                    :rules="
                      $_.concat(
                        [
                          `min_value:${item.unit_quantity}`,
                          `divisible_by:${item.unit_quantity}`
                        ],
                        [
                          item.max_order_quantity
                            ? `max_value:${item.max_order_quantity}`
                            : ''
                        ]
                      ).join('|')
                    "
                  >
                    <u-numeric-field
                      v-model.number="productConfigData.quantities[item.id]"
                      class="is-xsmall"
                      :label="$t('_.qty')"
                      :step="item.unit_quantity"
                      :min="item.unit_quantity"
                      :max="
                        item.max_order_quantity
                          ? item.max_order_quantity
                          : undefined
                      "
                      :disabled="disabled"
                      @click.native.stop.prevent
                    />
                  </ValidationProvider>
                </div>
              </template>
            </div>
          </template>
        </selector>
      </ValidationProvider>
    </b-field>
  </div>
</template>

<script lang="ts">
import type { PropType } from "vue";
import { defineComponent } from "vue";
import { mapGetters } from "vuex";
import HasValidation from "@/mixins/HasValidation";
import Context from "@/mixins/context";
import { BrandConfigKeys } from "@/data/constants";
import { PromotionDisplayTypes } from "@/data/enums/promotions";
import { ProductOrderTypes } from "@/data/enums";
import type { IProductConfigProviderData } from "@/models/providers/products";

export default defineComponent({
  name: "ProductOptions",
  components: {
    "editable-price": () =>
      import(
        "@/components/app/global/baskets/basket/productView/editablePrice.vue"
      )
  },
  mixins: [HasValidation, Context],
  inject: {
    $productConfigData: {
      from: "productConfigProviderData",
      type: Function as PropType<IProductConfigProviderData>
    }
  },
  props: {
    disabled: { type: [Boolean, Function], default: false },
    hidePrices: { type: Boolean, default: false }
  },
  data: () => ({
    orderTypes: ProductOrderTypes
  }),
  computed: {
    ...mapGetters({
      getBillingCycleName: "constants/getBillingCycleName",
      getCurrency: "constants/getCurrency"
    }),
    productConfigData() {
      return this.$productConfigData();
    }
  },
  methods: {
    handleClick($e, selected) {
      if (selected) {
        $e.preventDefault();
        $e.stopPropagation();
      }
    },
    getPromoTags(item) {
      const displayType =
        this.$store.getters["brand/config"][
          BrandConfigKeys.SHOW_PROMOTION_AS
        ] || PromotionDisplayTypes.PERCENTAGE;
      const tooltip = item.hasMixedPromotions
        ? this.$t("_sentence.promotion.discount_will_be_auto_applied")
        : item.saving_summary;
      switch (displayType) {
        case PromotionDisplayTypes.NAME:
          return this.$_.map(item.promotionsByName, promo => ({
            label: promo,
            tooltip
          }));
        case PromotionDisplayTypes.PERCENTAGE:
          return [
            {
              label: item.hasMixedPromotions
                ? this.$t("_.on_promotion")
                : this.$t("_.save_percentage", {
                    percentage: item.saving_formatted
                  }),
              tooltip
            }
          ];
        case PromotionDisplayTypes.LABEL:
        default:
          return [
            {
              label: this.$t("_.on_promotion"),
              tooltip
            }
          ];
      }
    }
  }
});
</script>

<style lang="scss" scoped>
.u-option-details {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 0.25rem 0.5rem;
}
</style>
